// graft(#97c): copied from genome/v4 sdk/src/genome/types.ts — v5 owns this module, no live dep on v3/v4.
/**
 * Genome v3 — Entity-Surface type system.
 *
 * Core types for the entity-surface architecture. A genome is
 * a flat map of entities with typed refs projected onto surfaces.
 *
 * @module genome/v3/types
 */

import type { JSONSchema7 } from '../schema/RuntimeTypes.js';

// ─── Entity Kinds ──────────────────────────────────────────────────

export const ENTITY_KINDS = [
  'component',
  'store',
  'schema',
  'service',
  'route',
  'event',
  'action',
  'effect',
  'plugin',
  'policy',      // @deprecated authoring (#S-4) — server-owned RBAC rule (role + grants); not author-instantiated
  'operation',   // v3.1 — endpoint-scoped service operation (accepts/produces refs)
  'query',       // Story D — reactive derived-value entity (consumes store/*)
  'binding',     // v3.1 §1.4 — implementation binding (provider + config) → service/*
  'credential',  // @deprecated authoring (#S-4) — server-owned typed secret reference; not author-instantiated
  'mount',       // M34 — fractal composition mount point (namespace + sub-genome ref)
  'layout',      // surface layout descriptor (dock zones, panel arrangement, responsive breakpoints)
] as const;

export type EntityKind = (typeof ENTITY_KINDS)[number];

// ─── Ref Relations ─────────────────────────────────────────────────

export const REF_RELS = [
  'binds',          // component → store
  'emits',          // component/action → event
  'listens',        // component/effect → event
  'consumes',       // component/store → service
  'produces',       // service/operation → schema (response)
  'accepts',        // service/operation → schema (request)
  'validates',      // store → schema
  'routes-to',      // route → component
  'extends',        // plugin → genome
  'slot',           // component → component (named slot)
  'triggers',       // event/action → action/effect
  'enforces',       // v3.1 — service/route/action → policy
  'implements-fsm', // v3.1 — effect → workflow (reserved seam for 1.3)
  'belongs-to',     // v3.1 — operation → service (parent linkage)
  'bound-to',       // v3.1 §1.4 — binding → service (DI implementation linkage)
  'bound-to-credential', // Phase 0 — service / operation / binding / action / effect → credential
  'composes',           // schema → schema (sub-schema composition, e.g. Part composes Port)
  'projects',           // layout → schema (a layout surface projects a schema, e.g. VR panel projects the same schema as the desktop surface)
  'mounts',             // M136 — mount → entity (a federated-remote mount point mounts a plugin descriptor surface)
  'references',         // #S-11 — schema → schema (a FK/relation: a source entity references a target via a named field; the relation graph the strand kernel's .relate() lowers, distinct from `composes` sub-schema containment)
] as const;

export type RefRel = (typeof REF_RELS)[number];

// ─── Policy meta shapes (v3.1 §1.1) ────────────────────────────────

/**
 * A grant pattern: `resource:action`, either side may be `*` (`correspondence:write`,
 * `accounts:*`, `*:read`). BRANDED (#1895-S2): a role name is not a grant, so a bare
 * `string` does not type-check here — cross only through `policyGrant()`.
 */
export type PolicyGrant = string & { readonly __policyGrant: true };

/**
 * Role-scoped policy meta. The canonical shape for a `policy/*` entity
 * whose `meta` declares a role plus its grant list. Used by
 * `PermissionsSurface.canPerform` to resolve actor capability.
 *
 * ```ts
 * // entity: policy/owner-base
 * {
 *   kind: 'policy',
 *   meta: {
 *     role: 'owner',
 *     grants: ['correspondence:*', 'accounts:*'],
 *   } satisfies PolicyRoleDef,
 * }
 * ```
 */
export interface PolicyRoleDef {
  /** Role identifier — matches the `actor` passed to canPerform */
  readonly role: string;
  /** Grant patterns in `resource:action` form */
  readonly grants: readonly PolicyGrant[];
  /** Optional human description of the policy */
  readonly description?: string;
}

/**
 * Explicit rule shape for a single (subject, action, resource) tuple.
 * Reserved for future fine-grained policies; not yet consumed by the
 * runtime but exported so ports can express them without reaching for
 * `meta.*` pass-through.
 */
export interface PolicyRuleDef {
  /** Who the rule applies to — usually a role id */
  readonly subject: string;
  /** What the subject may do — e.g. `read`, `write` */
  readonly action: string;
  /** On which resource — e.g. `correspondence`, `*` */
  readonly resource: string;
  /** `allow` (the default). ⚠ `deny` is UNEXPRESSIBLE by design — see `policy-grants.ts` (#1895-S2). */
  readonly effect?: 'allow';
}

/**
 * Typed meta contract for `policy/*` entities. Ports may attach either
 * `PolicyRoleDef` fields (role + grants) or a `rules` array of
 * fine-grained `PolicyRuleDef` entries, or both.
 */
export interface PolicyKindMeta extends Partial<PolicyRoleDef> {
  /** Optional fine-grained rules list */
  readonly rules?: readonly PolicyRuleDef[];
}

// ─── Effect meta shapes (v3.1 §1.2) ────────────────────────────────

/**
 * Transition between two states within a `StateMachineDef`. The
 * `target` string must resolve to a declared state id within the
 * enclosing machine. Optional `action` must resolve to an `action/*`
 * entity in the genome; optional `guard` names an action entity
 * (or predicate-shaped action) whose return value gates the step.
 *
 * ```ts
 * { target: 'active', action: 'action/activate' }
 * { target: 'frozen', action: 'action/freeze', guard: 'action/has-balance' }
 * ```
 */
export interface TransitionDef {
  /** State id of the transition destination (key in `StateMachineDef.states`) */
  readonly target: string;
  /** Optional action entity path, e.g. `action/activate` */
  readonly action?: string;
  /** Optional guard entity path — evaluated before the step */
  readonly guard?: string;
}

/**
 * A single state within a `StateMachineDef`. States carry the list of
 * transitions that fire from them; reachable state ids are the keys of
 * the enclosing `states` map.
 */
export interface StateDef {
  readonly transitions: readonly TransitionDef[];
}

/**
 * Typed FSM shape — declarative finite state machine embedded in an
 * `effect/*` entity's meta. Introduced in v3.1 §1.2 to replace the
 * free-form `effect.meta.stateMachine` passthrough.
 *
 * ```ts
 * // entity: effect/correspondence-lifecycle
 * {
 *   kind: 'effect',
 *   meta: {
 *     stateMachine: {
 *       initialState: 'draft',
 *       states: {
 *         draft: { transitions: [{ target: 'sent', action: 'action/send' }] },
 *         sent:  { transitions: [] },
 *       },
 *     } satisfies StateMachineDef,
 *   },
 * }
 * ```
 */
export interface StateMachineDef {
  /** State id the machine occupies on mount — must exist in `states` */
  readonly initialState: string;
  /** State id → definition map */
  readonly states: Readonly<Record<string, StateDef>>;
  /** Optional human description of the machine */
  readonly description?: string;
}

/**
 * Typed meta contract for `effect/*` entities. The only shipped typed
 * field today is `stateMachine`; the interface is extensible by design
 * so later v3.1 tiers can append more typed shapes without reshaping
 * existing effect ports.
 */
export interface EffectKindMeta {
  /** Optional typed state-machine declaration (v3.1 §1.2) */
  readonly stateMachine?: StateMachineDef;
}

// ─── Query meta shape (Story D — reactive derived values) ──────────

/**
 * Typed meta contract for `query/*` entities (Story D). A query
 * declares a derived value computed from one or more consumed stores.
 * The `expression` evaluates against the same binding scope the tree
 * renderer uses; `$store.*.*` bindings within it drive the reactive
 * dependency graph.
 *
 * ```yaml
 * # entity: query/today-spend
 * kind: query
 * refs:
 *   - { rel: consumes, target: store/accounts-store }
 * meta:
 *   description: "Sum of today's debit transactions"
 *   expression: { $expr: "$store['accounts-store'].todayTotal" }
 * ```
 */
export interface QueryKindMeta {
  /** Human description of the query */
  readonly description?: string;
  /**
   * The expression that computes the query's value. Evaluated via
   * the shared binding evaluator. May be a literal (rare — useful
   * for constants) or any `TreeNodeBinding`.
   */
  readonly expression?: TreeNodeBinding | TreeNodePropValue;
  readonly [key: string]: unknown;
}

/**
 * Typed meta contract for `action/*` entities (Story D).
 *
 * Actions declare a callable with typed args and an imperative body.
 * The body is a structured list of steps that the `ActionExecutor`
 * walks at invocation time. Each step is either a store mutation, an
 * event emit, a service call, or a state-machine send — the executor
 * picks the first `$*` discriminator present on the step.
 */
export interface ActionStepDef {
  /** Set a store key — `$set` is a dot-path starting with `{storeId}.` */
  readonly $set?: string;
  /** The value to write (literal or binding) */
  readonly value?: TreeNodePropValue;
  /** Emit an event — `$emit` is the event entity path (`event/foo`) */
  readonly $emit?: string;
  /** Payload for the emitted event (literal or binding) */
  readonly payload?: TreeNodePropValue;
  /** Call another action — transitively evaluated */
  readonly $action?: string;
  /** Args for the invoked action */
  readonly args?: Readonly<Record<string, TreeNodePropValue>>;
  /** Catch-all for future step kinds */
  readonly [key: string]: unknown;
}

export interface ActionKindMeta {
  /** Human description */
  readonly description?: string;
  /**
   * Typed arg schema. Keys are arg names; values declare the expected
   * shape. The simplest shape is a string type name (`'string'`,
   * `'number'`, `'boolean'`, `'object'`); the executor accepts any
   * JSON-schema-compatible object as an advanced escape hatch.
   */
  readonly args?: Readonly<
    Record<
      string,
      | 'string'
      | 'number'
      | 'boolean'
      | 'object'
      | 'array'
      | { readonly type: string; readonly enum?: readonly unknown[] }
    >
  >;
  /** Imperative body — executed in order */
  readonly body?: readonly ActionStepDef[];
  readonly [key: string]: unknown;
}

// ─── Component tree shapes (Story D — tree-authored components) ────

/**
 * Element selector for a `TreeNodeDef`. Three shapes are accepted so
 * the AST can target the three element sources the v3 runtime knows
 * how to resolve:
 *
 * - `'Grid'`, `'Container'`, `'Heading'`, … — a bare string is a
 *   built-in widget name (first letter capitalised by convention) or
 *   any component-registry kind that the designer's plugin registry
 *   exposes.
 * - `'component/subsystem-card'` — an entity path beginning with
 *   `component/` references another component entity in the genome.
 *   The resolver walks that entity (and its `meta.tree`, if any)
 *   recursively when rendering.
 * - `{ source: '<path>' }` — inline file reference (escape hatch) for
 *   source-backed leaves; the designer renders these as opaque nodes
 *   with an "Open source" action.
 */
export type TreeNodeElement =
  | string                                // built-in widget or `component/*` ref
  | { readonly source: string };          // inline file ref (escape hatch)

/**
 * A `$`-prefixed binding expression. Each shape routes evaluation
 * through `BindingEvaluator` (`runtime/v3/binding-evaluator`) to a
 * different resolver:
 *
 * - `{ $store: 'correspondence-store.items.length' }` — reads from the
 *   live Zustand store hydrator via dot-path.
 * - `{ $query: 'query/today-spend' }` — reads from the `QueryRuntime`
 *   cache, resolving the referenced `query/*` entity.
 * - `{ $var: 'item' }` — reads a variable that an outer `each` clause
 *   bound into the render scope.
 * - `{ $expr: 'store.count > 0 ? "many" : "none"' }` — evaluates an
 *   arbitrary expression via the shared expression engine (the same
 *   one powering `{{...}}` in v2) against the full scope.
 * - `{ $action: 'action/organiser.nav', args: { view: 'accounts' } }`
 *   — returns a callable suitable for binding to `onClick` /
 *   `onChange` etc. Args may themselves be `TreeNodePropValue`s so
 *   `$var` / `$store` references work inside action args.
 */
export type TreeNodeBinding =
  | { readonly $store: string }
  | { readonly $query: string }
  | { readonly $var: string }
  | { readonly $expr: string }
  | {
      readonly $action: string;
      readonly args?: Readonly<Record<string, unknown>>;
    };

/**
 * A single prop value. Either a JSON scalar (literal) or a binding
 * expression. Objects without a `$*` key are treated as literal object
 * values.
 */
export type TreeNodePropValue =
  | string
  | number
  | boolean
  | null
  | TreeNodeBinding
  | readonly TreeNodePropValue[]
  | Readonly<Record<string, unknown>>;

/**
 * A guard expression for `when` / iteration source for `each`. Shares
 * the same grammar as `TreeNodeBinding` — $store/$query/$var/$expr
 * bindings are all legal; $action is also legal for consistency
 * (treated as the callable's return value when evaluated).
 */
export type TreeNodeBindingExpr = TreeNodeBinding;

/**
 * Declarative tree node AST. Composable: children may be further
 * `TreeNodeDef`s, and named slots allow targeted injection into slot
 * sockets on the resolved element.
 *
 * ```yaml
 * # entity: component/dashboard-page (meta.tree)
 * tree:
 *   - element: VStack
 *     props: { gap: 24, padding: 16 }
 *     children:
 *       - element: Heading
 *         props: { text: Home, level: 1 }
 *       - element: Grid
 *         props: { columns: 3, gap: 12 }
 *         each:
 *           source: { $store: 'organiser-ui-store.subsystems' }
 *           as: 'item'
 *         children:
 *           - element: component/subsystem-card
 *             props:
 *               title: { $var: 'item.title' }
 *               icon:  { $var: 'item.icon' }
 * ```
 */
export interface TreeNodeDef {
  /** Element selector — built-in widget, component/* ref, or file source */
  readonly element: TreeNodeElement;
  /** Props map — literal values or binding expressions */
  readonly props?: Readonly<Record<string, TreeNodePropValue>>;
  /** Child nodes — rendered into the element's default slot */
  readonly children?: readonly TreeNodeDef[];
  /** Named slot fills — rendered into the element's named slot */
  readonly slots?: Readonly<Record<string, readonly TreeNodeDef[]>>;
  /** Render guard — node is skipped if this evaluates to falsy */
  readonly when?: TreeNodeBindingExpr;
  /**
   * Iteration clause. The node is cloned once per item in `source`,
   * with the item bound to `scope.vars[as]` (default: `'item'`) for
   * the duration of the clone's evaluation. `take` caps the iteration.
   */
  readonly each?: {
    readonly source: TreeNodeBindingExpr;
    readonly take?: number;
    readonly as?: string;
  };
}

/**
 * Typed meta contract for `component/*` entities. Components can either
 * expose a `meta.tree` AST — walked into a render-ready node tree by
 * `resolveTree()` (`@ubiclouds/v5-sdk/view-tree`, #1363-S1; before that slice it
 * named a function with ZERO implementations) — or declare a source-backed one via
 * `meta.source` (resolved by the component resolver chain). When both are present
 * `meta.tree` wins and `meta.source` is the designer's "Open source" reference.
 */
export interface ComponentKindMeta {
  /** Optional declarative tree AST (Story D) */
  readonly tree?: readonly TreeNodeDef[];
  /** Optional typed prop schema for the component */
  readonly props?: Readonly<Record<string, unknown>>;
  /** Optional file reference for source-backed components */
  readonly source?: string;
  /** Optional human label */
  readonly label?: string;
  /** Optional human title */
  readonly title?: string;
  /** Additional pass-through meta fields */
  readonly [key: string]: unknown;
}

// ─── Operation meta shapes (v3.1 §1.3) ─────────────────────────────

/**
 * HTTP method for a service operation. Free-form strings are also
 * accepted at the type boundary (clients may use custom verbs) but
 * the standard set covers every endpoint in the shipped ports.
 */
export type OperationMethod =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'PATCH'
  | 'DELETE'
  | 'HEAD'
  | 'OPTIONS';

/**
 * Typed meta contract for `operation/*` entities (v3.1 §1.3). Each
 * operation describes a single request/response pair on its parent
 * service — the HTTP verb, the path template (relative to the
 * service's baseUrl), and an optional human description. Request
 * and response schemas live on the entity's `refs[]` list as
 * `accepts → schema/*` and `produces → schema/*` respectively, so
 * the entity graph — not meta — is the authoritative source of
 * schema binding.
 *
 * ```ts
 * // entity: operation/list-correspondents
 * {
 *   kind: 'operation',
 *   refs: [
 *     { rel: 'belongs-to', target: 'service/organiser-api' },
 *     { rel: 'produces',   target: 'schema/correspondent' },
 *   ],
 *   meta: {
 *     method: 'GET',
 *     path: '/correspondents',
 *     description: 'List every correspondent known to the port',
 *   } satisfies OperationKindMeta,
 * }
 * ```
 */
export interface OperationKindMeta {
  /** HTTP verb (or free-form string for custom verbs) */
  readonly method?: OperationMethod | string;
  /** Path template, relative to the parent service's baseUrl */
  readonly path?: string;
  /** Optional human description of the operation */
  readonly description?: string;
  /** Response is an ARRAY of the single-ITEM `produces` (a LIST op): the runtime dispatch validates each element, not the whole array. #126-ARRAY. */
  readonly producesArray?: boolean;
}

// ─── Binding meta shapes (v3.1 §1.4) ───────────────────────────────

/**
 * Provider tag for a `binding/*` entity. Identifies the runtime
 * adapter that services the bound abstract API. The standard set
 * mirrors the v2 `services.implementations.<id>.provider` enum;
 * free-form strings are accepted for forward-compat with custom
 * providers (the v2 layer accepts arbitrary strings here too).
 */
export type BindingProvider =
  | 'fetch'
  | 'web-worker'
  | 'rest'
  | 'graphql'
  | 'grpc'
  | 'local'
  | 'container' // genome server runtime — a provider deployed as a Docker container (the backend default)
  | 'mock'
  | 'custom'
  | string;

/**
 * Typed meta contract for `binding/*` entities (v3.1 §1.4). A binding
 * declares the concrete provider and config that services an abstract
 * `service/*` entity — the entity-graph equivalent of a DI binding.
 * The parent `service/*` is identified by a `bound-to` ref on the
 * entity's `refs[]` list, mirroring how `operation/*` cites its parent
 * via `belongs-to`.
 *
 * ```ts
 * // entity: binding/layout-worker
 * {
 *   kind: 'binding',
 *   refs: [{ rel: 'bound-to', target: 'service/layout-engine' }],
 *   meta: {
 *     provider: 'web-worker',
 *     config: { workerScript: 'canvas-layout-worker.js', timeout: 10000 },
 *   } satisfies BindingKindMeta,
 * }
 * ```
 */
export interface BindingKindMeta {
  /** Provider tag — selects the runtime adapter (fetch, web-worker, …) */
  readonly provider?: BindingProvider;
  /** Provider-specific configuration (timeout, baseUrl, workerScript, …) */
  readonly config?: Readonly<Record<string, unknown>>;
  /** Optional human description of the binding */
  readonly description?: string;
  /** Additional pass-through meta fields */
  readonly [key: string]: unknown;
}

// ─── Credential meta shapes (Phase 0 — secrets-in-yaml design) ─────

/**
 * Typed credential category. Drives the Designer's form layout, the
 * runtime's input-validation rules (e.g. "this credential is a JWT —
 * must have 3 dot-separated parts"), and operator tooling that wants
 * to enumerate credentials by class.
 *
 * The set is closed by design — operators that need an unlisted shape
 * use `'generic-secret'` and put the typing on `valueSchema`.
 */
export type CredentialType =
  | 'bearer-token'      // OAuth2 access tokens, JWTs, opaque bearer tokens
  | 'oauth2-token'      // accessToken + refreshToken pair (Monzo / Gmail shape)
  | 'api-key'           // simple bearer-style API key
  | 'password'          // username/password pair
  | 'ssh-key'           // ed25519 / rsa private key
  | 'tls-cert'          // PEM cert + key pair
  | 'generic-secret';   // free-form bytes — typing falls to `valueSchema`

/**
 * Typed meta contract for `credential/*` entities (Phase 0 of the
 * secrets-in-genome-yaml design — see
 * `dev/2026-05-01-v3-secrets-in-genome-yaml-design.md`).
 *
 * A `credential/*` entity declares the EXISTENCE and TYPED SHAPE of a
 * secret a genome consumes — it NEVER carries the secret bytes. The
 * plaintext lives exclusively in the per-user SecretStore blob at
 * `data/secrets/{userHash}/{credentialId}.bin`. The entity-graph
 * carries only typed metadata so the validator, the Designer, and the
 * runtime can reason about secret consumption without seeing
 * plaintext.
 *
 * Consumers (`service/*`, `operation/*`, `binding/*`, `action/*`,
 * `effect/*`) reference the credential through a typed
 * `bound-to-credential` ref — replacing the legacy `tokenFrom: vault`
 * magic-string convention.
 *
 * ```ts
 * // entity: credential/monzo-access-token
 * {
 *   kind: 'credential',
 *   meta: {
 *     credentialId: 'monzo-access-token',
 *     type: 'oauth2-token',
 *     description: 'Monzo OAuth2 access + refresh token pair',
 *     rotationHint: '90d',
 *   } satisfies CredentialKindMeta,
 * }
 * ```
 */
export interface CredentialKindMeta {
  /**
   * SecretStore credentialId — the lookup key for the per-user
   * AES-256-GCM blob. The bytes live there, NOT in this YAML.
   * Conventionally matches the entity `id` segment but kept separate
   * so a single SecretStore credential can back multiple genome
   * entities (and so the entity id can be stable across rotations
   * that re-namespace the underlying credentialId).
   */
  readonly credentialId: string;
  /**
   * Typed credential category — drives Designer icon, format hints,
   * and runtime input validation. See `CredentialType` for the
   * closed enum.
   */
  readonly type: CredentialType;
  /** Optional human-readable description. */
  readonly description?: string;
  /**
   * Optional rotation policy hint. Free-form string read by tooling
   * that surfaces "credential is X days old" warnings — `'90d'`,
   * `'30d'`, `'manual'`, `'never'` are conventional values. Not
   * enforced by the runtime; informational only.
   */
  readonly rotationHint?: string;
  /** Additional pass-through meta fields (provider tag, scope, …). */
  readonly [key: string]: unknown;
}

// ─── Entity Ref ────────────────────────────────────────────────────

export interface EntityRef {
  readonly rel: RefRel;
  readonly target: string;           // entity path: {kind}/{id}
  readonly params?: Readonly<Record<string, unknown>>;
}

// ─── Entity Def ────────────────────────────────────────────────────

export interface EntityDef {
  readonly kind: EntityKind;
  readonly schema?: JSONSchema7;
  readonly refs?: readonly EntityRef[];
  readonly meta?: Readonly<Record<string, unknown>>; // incl. #453 sourceRef (./source-ref.ts)
}

// ─── Genome Archetypes ─────────────────────────────────────────────

export const GENOME_ARCHETYPES = [
  'application',
  'plugin',
  'component',
  'service',
  'template',
] as const;

export type GenomeArchetype = (typeof GENOME_ARCHETYPES)[number];

// ─── Mount Declaration (plugin archetype) ──────────────────────────

export interface MountDeclaration {
  readonly point: string;            // target entity path in host
  readonly slot: string;             // named slot on the target
  readonly provides: string;         // entity path in this genome
}

// ─── Surface Config ────────────────────────────────────────────────

export interface ReactSurfaceConfig {
  readonly theme?: string;
  readonly layout?: {
    readonly type: 'dock' | 'split' | 'tabs' | 'stack';
    readonly zones?: Readonly<Record<string, ZoneConfig>>;
  };
}

export interface ZoneConfig {
  readonly dock?: 'top' | 'left' | 'center' | 'right' | 'bottom';
  readonly width?: number;
  readonly height?: number;
  readonly resizable?: boolean;
  readonly collapsible?: boolean;
}

export interface McpSurfaceConfig {
  readonly transport?: 'stdio' | 'http' | 'websocket';
  readonly expose?: readonly string[];  // glob patterns for entity exposure
}

export interface ServiceSurfaceConfig {
  readonly port?: number;
  readonly auth?: {
    readonly provider: string;
    readonly audience?: string;
  };
}

/**
 * Per-store persistence adapter declaration. The canonical shape of
 * one entry in `PersistenceSurfaceConfig.adapters`. `kind` references
 * a factory registered on the PersistenceSurface context; every
 * other field is adapter-specific config (path, connectionString,
 * debounceMs, …).
 */
export interface PersistenceAdapterDeclaration {
  readonly kind: string;
  readonly [key: string]: unknown;
}

export interface PersistenceSurfaceConfig {
  /** Legacy top-level adapter (v3.0 shape, still accepted). */
  readonly adapter?: string;
  /** Legacy top-level connection string (v3.0 shape, still accepted). */
  readonly connectionString?: string;
  /**
   * Per-store adapter declarations keyed by full entity path
   * (e.g. `store/accounts`). Consumed by the PersistenceSurface
   * walker — see `runtime/v3/persistence-surface-walker.ts`.
   */
  readonly adapters?: Readonly<Record<string, PersistenceAdapterDeclaration>>;
  readonly [key: string]: unknown;
}

/**
 * Data-source profile — configures `ServiceMockRegistry` /
 * `FetchInterceptor` to seed stores and route service calls at runtime.
 * Profiles are declarative host config, not first-class genome entities,
 * so they live under `surfaces.data.profiles` (the canonical location).
 */
export interface DataProfileConfig {
  readonly name?: string;
  readonly icon?: string;
  readonly description?: string;
  readonly defaultServiceMode?: 'mock' | 'local' | 'live';
  readonly stores?: Readonly<Record<string, Readonly<Record<string, unknown>>>>;
  readonly [key: string]: unknown;
}

/**
 * `surfaces.data` — data-surface configuration. The canonical location
 * for data-source profiles. There is **no** top-level `data` block on
 * `GenomeV3` — profiles live here because `surfaces` is where
 * declarative host config belongs (see the `GenomeV3` JSDoc for the
 * extensibility rationale).
 */
export interface DataSurfaceConfig {
  readonly profiles?: Readonly<Record<string, DataProfileConfig>>;
  readonly [key: string]: unknown;
}

export interface SurfacesConfig {
  readonly react?: ReactSurfaceConfig;
  readonly mcp?: McpSurfaceConfig;
  readonly service?: ServiceSurfaceConfig;
  readonly persistence?: PersistenceSurfaceConfig;
  readonly data?: DataSurfaceConfig;
  readonly [key: string]: unknown;     // extensible for future surfaces
}

// ─── GenomeV3 ──────────────────────────────────────────────────────

/**
 * Root `GenomeV3` shape.
 *
 * ### Extensibility decision (2026-04-22)
 *
 * There is intentionally NO top-level `data` key on `GenomeV3`. The v2
 * genome had a `data: { profiles, stores, queries, … }` block; v3
 * migrated the flat entity map into `entities`, and data-source
 * profiles (the only remaining non-entity "data" config) live under
 * `surfaces.data.profiles` — the canonical location.
 *
 * Rationale: `surfaces` is declarative host config (how the
 * entity-surface runtime projects entities onto React, MCP, service,
 * persistence, and data adapters). Data-source profiles are consumed
 * by the data surface (`ServiceMockRegistry` + `FetchInterceptor`),
 * not by the entity graph itself, so they belong with other surface
 * configs — not as a second top-level bucket competing with
 * `entities`.
 *
 * The `GenomeBuilder.profiles(map)` convenience writes into
 * `surfaces.data.profiles` to reflect this.
 */
export interface GenomeV3 {
  readonly v: 3;
  readonly archetype: GenomeArchetype;
  readonly id: string;
  readonly name: string;
  readonly version: string;

  /** Flat entity map — keys are entity paths: `{kind}/{id}` */
  readonly entities: Readonly<Record<string, EntityDef>>;

  /** Per-surface configuration (data-source profiles live at `surfaces.data.profiles`) */
  readonly surfaces?: SurfacesConfig;

  /** Mount points (plugin archetype only) */
  readonly mounts?: readonly MountDeclaration[];
}

// ─── Validation ────────────────────────────────────────────────────

export interface RefError {
  readonly entityPath: string;
  readonly refIndex: number;
  readonly rel: RefRel;
  readonly target: string;
  readonly message: string;
}

export interface ValidationResult {
  readonly valid: boolean;
  readonly errors: readonly RefError[];
  readonly warnings: readonly RefError[];
}

// ─── Surface contract ──────────────────────────────────────────────

export interface Surface<TContext = unknown> {
  readonly id: string;
  readonly resolves: readonly EntityKind[];
  resolve(entity: EntityDef, path: string, ctx: TContext): unknown;
  /**
   * Mount the surface. Surfaces that do synchronous work (MCP,
   * React) return the handle directly; surfaces that must await
   * external I/O at mount time (e.g. PersistenceSurface opening DB
   * connections) return a Promise for the handle. Callers using
   * the generic contract should always `await` the result.
   */
  mount(genome: GenomeV3, ctx: TContext): { dispose(): void } | Promise<{ dispose(): void }>;
}
